import { Card } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import { useAppSelector } from "@/store/store"
import { calculateCollectionTotals } from "@/utils/functions"
import { Button } from "@okeys/shared-react-components"
import { Check, ChevronRight } from "lucide-react"
import React, { useMemo } from "react"

export type StepperProps = {
  steps: Array<{
    id: string
    title: string
    description?: string
  }>
  currentStep: string
  onStepChange?: (stepId: string) => void
  className?: string
}

export const Stepper: React.FC<StepperProps> = ({
  steps,
  currentStep,
  onStepChange,
  className,
}) => {
  const currentStepIndex = steps.findIndex((step) => step.id === currentStep)

  return (
    <div className={cn("w-full", className)}>
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const isActive = step.id === currentStep
          const isCompleted = index < currentStepIndex
          const isClickable = onStepChange && (isCompleted || isActive)

          return (
            <React.Fragment key={step.id}>
              {/* Step */}
              <div className="flex flex-row items-center justify-center gap-2">
                <button
                  onClick={() => isClickable && onStepChange?.(step.id)}
                  disabled={!isClickable}
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-full border-2 text-sm font-semibold transition-all duration-200",
                    {
                      // Completed step
                      "border-primary bg-primary text-white hover:bg-primary/80":
                        isCompleted,
                      // Active step
                      "border-primary bg-primary/10 text-primary font-bold ring-6 ring-primary/30":
                        isActive,
                      // Inactive step
                      "border-gray-500 bg-primary/10 text-gray-500 font-bold":
                        !isActive && !isCompleted,
                      // Clickable
                      "cursor-pointer hover:border-primary/80": isClickable,
                      // Not clickable
                      "cursor-not-allowed": !isClickable,
                    }
                  )}
                >
                  {isCompleted ? (
                    <Check className="h-5 w-5" />
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </button>

                {/* Step info */}
                <div className="text-center">
                  <div
                    className={cn("text-sm font-medium", {
                      "text-primary": isActive,
                      "text-gray-900": isCompleted,
                      "text-gray-500": !isActive && !isCompleted,
                    })}
                  >
                    {step.title}
                  </div>
                  {step.description && (
                    <div
                      className={cn("text-xs mt-1", {
                        "text-primary/80": isActive,
                        "text-gray-600": isCompleted,
                        "text-gray-400": !isActive && !isCompleted,
                      })}
                    >
                      {step.description}
                    </div>
                  )}
                </div>
              </div>

              {/* Separator */}
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    "flex-1 h-0.5 mx-4 transition-all duration-200",
                    {
                      "bg-primary": index < currentStepIndex,
                      "bg-gray-500": index >= currentStepIndex,
                    }
                  )}
                />
              )}
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}

export type StepperNavigationProps = {
  canGoBack: boolean
  canGoNext: boolean
  onBack: () => void
  onNext: () => void
  onFinish?: () => void
  isLastStep?: boolean
  backLabel?: string
  nextLabel?: string
  finishLabel?: string
  className?: string
}

export const StepperNavigation: React.FC<StepperNavigationProps> = ({
  canGoBack,
  canGoNext,
  onBack,
  onNext,
  onFinish,
  isLastStep = false,
  backLabel = "Précédent",
  nextLabel = "Suivant",
  finishLabel = "Terminer",
  className,
}) => {
  const { collectionInfo } = useAppSelector((state) => state.cashRegister)

  const { totalServicesPrice, totalPromotionDiscount, totalPrice } =
    useMemo(() => {
      if (!collectionInfo?.serviceWithPromo.length) {
        return {
          totalServicesPrice: 0,
          totalPromotionDiscount: 0,
          totalPrice: 0,
        }
      }
      return calculateCollectionTotals(collectionInfo.serviceWithPromo)
    }, [collectionInfo?.serviceWithPromo])

  return (
    <div className={cn("flex justify-between items-center", className)}>
      <Button onClick={onBack} disabled={!canGoBack} variant="outline">
        <ChevronRight className="h-4 w-4 rotate-180" />
        {backLabel}
      </Button>

      <Card className="border-primary/20 bg-primary/5 max-h-[45px] px-4 py-2">
        <div className="pt-0 m-0 py-0 max-h-[35px]">
          <div className="flex flex-row justify-between items-center max-h-[35px]">
            {totalPromotionDiscount > 0 ? (
              <div className="space-y-1 text-sm flex flex-row justify-between items-center gap-2">
                <div className="flex justify-between">
                  <span className="font-bold text-xl">
                    Total : {totalServicesPrice.toFixed(2)} €
                  </span>
                </div>
                <span className="font-bold text-xl">-</span>
                <div className="flex justify-between text-green-600">
                  <span className="font-bold text-xl">
                    Remises : {totalPromotionDiscount.toFixed(2)} €
                  </span>
                </div>
                <span className="font-bold text-xl">-</span>
                <div className="flex justify-between items-center">
                  <span className="font-bold text-xl text-primary">
                    A payer : {totalPrice.toFixed(2)} €
                  </span>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex justify-between items-center">
                  <span className="font-bold text-xl text-primary">
                    A payer : {totalPrice.toFixed(2)} €
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      <Button onClick={isLastStep ? onFinish : onNext} disabled={!canGoNext}>
        {isLastStep ? finishLabel : nextLabel}
        {!isLastStep && <ChevronRight className="h-4 w-4" />}
      </Button>
    </div>
  )
}
